'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

type View = 'landing' | 'assessment' | 'results';

interface Question {
  id: string;
  text: string;
  principle: string;
  weight: number;
}

interface Section {
  id: string;
  title: string;
  description: string;
  questions: Question[];
}

const sections: Section[] = [
  {
    id: 'transparency',
    title: 'Transparency & Explainability',
    description: 'Making AI systems understandable and their decisions explainable',
    questions: [
      { id: 'tr1', text: 'Do you disclose to users when they are interacting with AI systems?', principle: 'Transparency', weight: 10 },
      { id: 'tr2', text: 'Can you explain how AI systems make decisions in understandable terms?', principle: 'Explainability', weight: 10 },
      { id: 'tr3', text: 'Is documentation maintained about AI model training data and methodology?', principle: 'Documentation', weight: 9 },
      { id: 'tr4', text: 'Do you disclose the limitations and potential failure modes of AI systems?', principle: 'Transparency', weight: 9 },
      { id: 'tr5', text: 'Are AI-generated outputs clearly labeled as such?', principle: 'Transparency', weight: 9 },
      { id: 'tr6', text: 'Is there traceability from AI outputs back to training decisions?', principle: 'Auditability', weight: 8 },
    ],
  },
  {
    id: 'fairness',
    title: 'Fairness & Non-Discrimination',
    description: 'Ensuring AI systems treat all people fairly and without bias',
    questions: [
      { id: 'fr1', text: 'Do you test AI systems for bias across protected characteristics?', principle: 'Bias Testing', weight: 10 },
      { id: 'fr2', text: 'Is training data audited for representation and historical biases?', principle: 'Data Quality', weight: 10 },
      { id: 'fr3', text: 'Do you have metrics to measure fairness across different groups?', principle: 'Measurement', weight: 9 },
      { id: 'fr4', text: 'Are there processes to remediate identified biases?', principle: 'Remediation', weight: 9 },
      { id: 'fr5', text: 'Do you monitor AI systems in production for emerging biases?', principle: 'Monitoring', weight: 9 },
      { id: 'fr6', text: 'Are impacted communities consulted about AI system design?', principle: 'Inclusion', weight: 8 },
    ],
  },
  {
    id: 'accountability',
    title: 'Accountability & Governance',
    description: 'Clear ownership and responsibility for AI systems',
    questions: [
      { id: 'ac1', text: 'Is there clear ownership and accountability for each AI system?', principle: 'Ownership', weight: 10 },
      { id: 'ac2', text: 'Do you have an AI ethics board or review committee?', principle: 'Governance', weight: 8 },
      { id: 'ac3', text: 'Are there escalation paths for AI-related ethical concerns?', principle: 'Escalation', weight: 9 },
      { id: 'ac4', text: 'Is there executive-level oversight of AI initiatives?', principle: 'Leadership', weight: 8 },
      { id: 'ac5', text: 'Do you maintain audit trails of AI system decisions?', principle: 'Auditability', weight: 9 },
      { id: 'ac6', text: 'Are there consequences for misuse or harm from AI systems?', principle: 'Accountability', weight: 8 },
    ],
  },
  {
    id: 'human-oversight',
    title: 'Human Oversight & Control',
    description: 'Maintaining meaningful human control over AI systems',
    questions: [
      { id: 'ho1', text: 'Is there human oversight for high-stakes AI decisions?', principle: 'Human Control', weight: 10 },
      { id: 'ho2', text: 'Can humans override or correct AI system outputs?', principle: 'Override', weight: 10 },
      { id: 'ho3', text: 'Are there defined thresholds for when human review is required?', principle: 'Escalation', weight: 9 },
      { id: 'ho4', text: 'Do humans have sufficient information to evaluate AI recommendations?', principle: 'Information', weight: 9 },
      { id: 'ho5', text: 'Are there kill switches or shutdown procedures for AI systems?', principle: 'Safety', weight: 8 },
      { id: 'ho6', text: 'Is the level of autonomy appropriate for the risk level?', principle: 'Proportionality', weight: 9 },
    ],
  },
  {
    id: 'privacy',
    title: 'Privacy & Data Protection',
    description: 'Protecting individual privacy in AI systems',
    questions: [
      { id: 'pv1', text: 'Is personal data minimized in AI training and operation?', principle: 'Data Minimization', weight: 10 },
      { id: 'pv2', text: 'Do individuals have rights regarding AI processing of their data?', principle: 'Data Rights', weight: 10 },
      { id: 'pv3', text: 'Is consent obtained for personal data use in AI systems?', principle: 'Consent', weight: 9 },
      { id: 'pv4', text: 'Are there privacy impact assessments for AI systems?', principle: 'Assessment', weight: 9 },
      { id: 'pv5', text: 'Is personal data protected from unauthorized access in AI systems?', principle: 'Security', weight: 9 },
      { id: 'pv6', text: 'Can AI systems infer sensitive information about individuals?', principle: 'Inference', weight: 8 },
    ],
  },
  {
    id: 'safety',
    title: 'Safety & Reliability',
    description: 'Ensuring AI systems operate safely and reliably',
    questions: [
      { id: 'sf1', text: 'Are AI systems tested for robustness and edge cases?', principle: 'Robustness', weight: 10 },
      { id: 'sf2', text: 'Are there safeguards against adversarial attacks?', principle: 'Security', weight: 9 },
      { id: 'sf3', text: 'Is AI system performance monitored in production?', principle: 'Monitoring', weight: 9 },
      { id: 'sf4', text: 'Are there fail-safe mechanisms for AI system failures?', principle: 'Fail-Safe', weight: 9 },
      { id: 'sf5', text: 'Is there red-teaming or adversarial testing of AI systems?', principle: 'Testing', weight: 8 },
      { id: 'sf6', text: 'Are AI systems tested for unintended consequences?', principle: 'Impact Assessment', weight: 8 },
    ],
  },
  {
    id: 'societal-impact',
    title: 'Societal & Environmental Impact',
    description: 'Considering broader impacts of AI systems',
    questions: [
      { id: 'si1', text: 'Do you assess the societal impact of AI systems before deployment?', principle: 'Impact Assessment', weight: 9 },
      { id: 'si2', text: 'Is the environmental impact of AI systems considered?', principle: 'Environment', weight: 8 },
      { id: 'si3', text: 'Do you consider effects on employment and labor?', principle: 'Employment', weight: 8 },
      { id: 'si4', text: 'Is there a process for stakeholder feedback on AI systems?', principle: 'Engagement', weight: 8 },
      { id: 'si5', text: 'Do you consider potential misuse of AI systems?', principle: 'Dual Use', weight: 9 },
      { id: 'si6', text: 'Are beneficial applications of AI prioritized?', principle: 'Benefit', weight: 7 },
    ],
  },
  {
    id: 'lifecycle',
    title: 'AI Lifecycle Management',
    description: 'Managing AI systems throughout their lifecycle',
    questions: [
      { id: 'lc1', text: 'Is there an ethical review process before AI development?', principle: 'Pre-Development', weight: 9 },
      { id: 'lc2', text: 'Are ethical considerations part of AI system design?', principle: 'Design', weight: 9 },
      { id: 'lc3', text: 'Is there ethics review before AI deployment?', principle: 'Deployment', weight: 9 },
      { id: 'lc4', text: 'Are AI systems regularly reviewed for ethical performance?', principle: 'Review', weight: 8 },
      { id: 'lc5', text: 'Is there a process for retiring AI systems responsibly?', principle: 'Retirement', weight: 7 },
      { id: 'lc6', text: 'Are lessons learned documented and incorporated?', principle: 'Learning', weight: 8 },
    ],
  },
];

export default function AIEthicsAssessment() {
  const [view, setView] = useState<View>('landing');
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState<Record<string, 'yes' | 'no' | 'partial' | 'na'>>({});
  const [aiUseCases, setAiUseCases] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'ai-ethics-assessment',
    toolCategory: 'compliance',
  });

  const handleAnswer = (questionId: string, value: 'yes' | 'no' | 'partial' | 'na') => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleUseCaseToggle = (useCase: string) => {
    setAiUseCases((prev) =>
      prev.includes(useCase) ? prev.filter((u) => u !== useCase) : [...prev, useCase]
    );
  };

  const calculateScore = () => {
    let earned = 0;
    let possible = 0;
    const gaps: { principle: string; question: string; status: string }[] = [];

    sections.forEach((section) => {
      section.questions.forEach((q) => {
        const answer = answers[q.id];
        if (answer === 'na') return;

        possible += q.weight;
        if (answer === 'yes') {
          earned += q.weight;
        } else if (answer === 'partial') {
          earned += q.weight * 0.5;
          gaps.push({ principle: q.principle, question: q.text, status: 'Partial' });
        } else if (answer === 'no') {
          gaps.push({ principle: q.principle, question: q.text, status: 'Not Implemented' });
        }
      });
    });

    return {
      score: possible > 0 ? Math.round((earned / possible) * 100) : 0,
      gaps,
    };
  };

  const getSectionScore = (sectionId: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return 0;

    let earned = 0;
    let possible = 0;

    section.questions.forEach((q) => {
      const answer = answers[q.id];
      if (answer === 'na') return;
      possible += q.weight;
      if (answer === 'yes') earned += q.weight;
      else if (answer === 'partial') earned += q.weight * 0.5;
    });

    return possible > 0 ? Math.round((earned / possible) * 100) : 0;
  };

  const getMaturityLevel = (score: number) => {
    if (score >= 90) return { level: 'Exemplary', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (score >= 75) return { level: 'Mature', color: 'text-blue-400', bg: 'bg-blue-500/20' };
    if (score >= 60) return { level: 'Developing', color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    if (score >= 40) return { level: 'Basic', color: 'text-accent-400', bg: 'bg-accent-500/20' };
    return { level: 'Initial', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  const exportToMarkdown = () => {
    const { score, gaps } = calculateScore();
    const maturity = getMaturityLevel(score);

    let md = `# AI Ethics Assessment Report\n\n`;
    md += `**Generated:** ${new Date().toLocaleDateString()}\n\n`;

    if (aiUseCases.length > 0) {
      md += `**AI Use Cases:** ${aiUseCases.join(', ')}\n\n`;
    }

    md += `## Overall Ethics Maturity\n\n`;
    md += `**Score:** ${score}%\n`;
    md += `**Maturity Level:** ${maturity.level}\n\n`;

    md += `## Principle Scores\n\n`;
    md += `| Principle Area | Score |\n`;
    md += `|----------------|-------|\n`;
    sections.forEach((section) => {
      md += `| ${section.title} | ${getSectionScore(section.id)}% |\n`;
    });

    if (gaps.length > 0) {
      md += `\n## Ethics Gaps\n\n`;
      md += `| Principle | Issue | Status |\n`;
      md += `|-----------|-------|--------|\n`;
      gaps.forEach((gap) => {
        md += `| ${gap.principle} | ${gap.question} | ${gap.status} |\n`;
      });
    }

    md += `\n## Relevant Frameworks & Guidelines\n\n`;
    md += `- **OECD AI Principles:** Human-centered values, transparency, accountability\n`;
    md += `- **EU AI Act:** Risk-based regulation with prohibited practices\n`;
    md += `- **IEEE Ethically Aligned Design:** Prioritizing human well-being\n`;
    md += `- **NIST AI RMF:** Risk management framework for AI systems\n`;
    md += `- **UNESCO AI Ethics:** Human rights and sustainability focus\n`;

    md += `\n## Recommended Actions\n\n`;
    if (score < 75) {
      md += `1. Establish AI ethics governance structure\n`;
      md += `2. Implement bias testing for all AI systems\n`;
      md += `3. Create transparency disclosures for AI use\n`;
      md += `4. Define human oversight requirements\n`;
      md += `5. Develop AI ethics training program\n`;
      md += `6. Create stakeholder feedback mechanisms\n`;
    } else {
      md += `1. Maintain current ethics practices\n`;
      md += `2. Continue monitoring for emerging concerns\n`;
      md += `3. Share best practices across organization\n`;
      md += `4. Engage with external ethics communities\n`;
    }

    md += `\n---\n*Assessment conducted using The Art of CTO AI Ethics Assessment*\n`;

    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-ethics-assessment-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const { score, gaps: calculatedGaps } = calculateScore();
    const maturity = getMaturityLevel(score);

    // Build scores from section scores
    const scores: Record<string, number> = {
      overall: score,
    };
    sections.forEach((section) => {
      scores[section.id] = getSectionScore(section.id);
    });

    // Build gaps from calculated gaps
    const gaps = calculatedGaps.map((gap, index) => ({
      id: `gap-${index}`,
      title: gap.question,
      severity: (gap.status === 'Not Implemented' ? 'high' : 'medium') as 'critical' | 'high' | 'medium' | 'low',
      description: `Ethics principle: ${gap.principle}`,
      recommendation: `Address this gap to improve AI ethics compliance`,
    }));

    // Build recommendations based on score
    const recommendations = score < 75
      ? [
          { id: 'rec-1', title: 'Establish AI ethics governance structure', priority: 'high' as const, description: 'Create formal oversight for AI ethics', effort: 'High' },
          { id: 'rec-2', title: 'Implement bias testing for all AI systems', priority: 'high' as const, description: 'Ensure fairness across all AI implementations', effort: 'Medium' },
          { id: 'rec-3', title: 'Create transparency disclosures for AI use', priority: 'medium' as const, description: 'Inform users about AI interactions', effort: 'Low' },
          { id: 'rec-4', title: 'Define human oversight requirements', priority: 'high' as const, description: 'Establish when human review is required', effort: 'Medium' },
          { id: 'rec-5', title: 'Develop AI ethics training program', priority: 'medium' as const, description: 'Educate staff on responsible AI practices', effort: 'Medium' },
        ]
      : [
          { id: 'rec-1', title: 'Maintain current ethics practices', priority: 'low' as const, description: 'Continue existing governance', effort: 'Low' },
          { id: 'rec-2', title: 'Continue monitoring for emerging concerns', priority: 'medium' as const, description: 'Stay ahead of new ethical challenges', effort: 'Low' },
        ];

    const assessment = await saveResult({
      name: `AI Ethics Assessment - ${new Date().toLocaleDateString()}`,
      score_overall: score,
      scores,
      answers,
      gaps,
      recommendations,
      metadata: {
        maturityLevel: maturity.level,
        aiUseCases,
        totalQuestions: sections.reduce((sum, s) => sum + s.questions.length, 0),
        answeredQuestions: Object.keys(answers).length,
        gapCount: gaps.length,
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  if (view === 'landing') {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Link href="/tools" className="text-foreground-tertiary hover:text-accent mb-8 inline-block">
            ← Back to Tools
          </Link>

          <div className="text-center mb-12">
            <span className="text-accent text-sm font-medium tracking-wider uppercase">
              Responsible AI
            </span>
            <h1 className="text-4xl font-bold text-foreground mt-2 mb-4">
              AI Ethics Assessment
            </h1>
            <p className="text-foreground-secondary text-lg max-w-2xl mx-auto">
              Evaluate your AI systems against ethical principles including transparency,
              fairness, accountability, and human oversight. Build responsible AI practices.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-surface border border-border-muted rounded-lg p-6">
              <h3 className="text-foreground font-semibold mb-3">Why AI Ethics Matter</h3>
              <p className="text-foreground-secondary text-sm">
                AI systems can perpetuate biases, make opaque decisions, and impact
                individuals and society. Ethical AI practices build trust, ensure
                compliance, and prevent harm.
              </p>
            </div>
            <div className="bg-surface border border-border-muted rounded-lg p-6">
              <h3 className="text-foreground font-semibold mb-3">Regulatory Context</h3>
              <p className="text-foreground-secondary text-sm">
                The EU AI Act, NIST AI RMF, and other frameworks are establishing
                requirements for responsible AI. Proactive ethics practices prepare
                you for regulatory compliance.
              </p>
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h3 className="text-foreground font-semibold mb-4">Select Your AI Use Cases</h3>
            <div className="grid md:grid-cols-2 gap-2">
              {[
                'Decision Support Systems',
                'Customer Service Chatbots',
                'Content Generation',
                'Recommendation Systems',
                'Fraud Detection',
                'Hiring & HR Systems',
                'Credit & Lending Decisions',
                'Healthcare Diagnostics',
                'Predictive Analytics',
                'Computer Vision',
                'Natural Language Processing',
                'Autonomous Systems',
              ].map((useCase) => (
                <button
                  key={useCase}
                  onClick={() => handleUseCaseToggle(useCase)}
                  className={`px-3 py-2 rounded text-sm text-left transition-colors ${
                    aiUseCases.includes(useCase)
                      ? 'bg-accent-muted text-accent border border-border-muted0'
                      : 'bg-cream/5 text-foreground-tertiary border border-border-muted hover:border-border/30'
                  }`}
                >
                  {useCase}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h3 className="text-foreground font-semibold mb-3">Key Ethics Principles</h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-foreground-secondary">
              <div>
                <p className="mb-2"><span className="text-accent">•</span> Transparency & Explainability</p>
                <p className="mb-2"><span className="text-accent">•</span> Fairness & Non-Discrimination</p>
                <p className="mb-2"><span className="text-accent">•</span> Accountability & Governance</p>
                <p className="mb-2"><span className="text-accent">•</span> Human Oversight & Control</p>
              </div>
              <div>
                <p className="mb-2"><span className="text-accent">•</span> Privacy & Data Protection</p>
                <p className="mb-2"><span className="text-accent">•</span> Safety & Reliability</p>
                <p className="mb-2"><span className="text-accent">•</span> Societal & Environmental Impact</p>
                <p className="mb-2"><span className="text-accent">•</span> Lifecycle Management</p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => setView('assessment')}
              className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-8 py-3 rounded-lg transition-colors"
            >
              Start Ethics Assessment
            </button>
            <p className="text-foreground-tertiary text-sm mt-4">
              Takes approximately 15-20 minutes to complete
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'assessment') {
    const section = sections[currentSection];
    const progress = ((currentSection + 1) / sections.length) * 100;

    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => setView('landing')}
              className="text-foreground-tertiary hover:text-accent"
            >
              ← Back
            </button>
            <span className="text-foreground-tertiary text-sm">
              Section {currentSection + 1} of {sections.length}
            </span>
          </div>

          <div className="mb-8">
            <div className="flex justify-between text-sm text-foreground-tertiary mb-2">
              <span>{section.title}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-cream/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-accent transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-foreground mb-2">{section.title}</h2>
            <p className="text-foreground-tertiary">{section.description}</p>
          </div>

          <div className="space-y-4">
            {section.questions.map((question) => (
              <div
                key={question.id}
                className="bg-surface border border-border-muted rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <span className="text-accent text-xs font-medium">
                      {question.principle}
                    </span>
                    <p className="text-foreground mt-1">{question.text}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {(['yes', 'partial', 'no', 'na'] as const).map((value) => (
                    <button
                      key={value}
                      onClick={() => handleAnswer(question.id, value)}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        answers[question.id] === value
                          ? value === 'yes'
                            ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                            : value === 'partial'
                            ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                            : value === 'no'
                            ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                            : 'bg-gray-500/20 text-gray-400 border border-gray-500/50'
                          : 'bg-cream/5 text-foreground-tertiary border border-border-muted hover:border-border/30'
                      }`}
                    >
                      {value === 'na' ? 'N/A' : value.charAt(0).toUpperCase() + value.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between mt-8">
            <button
              onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
              disabled={currentSection === 0}
              className="px-6 py-2 rounded-lg border border-border text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:border-border/40 transition-colors"
            >
              Previous
            </button>
            {currentSection < sections.length - 1 ? (
              <button
                onClick={() => setCurrentSection(currentSection + 1)}
                className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-6 py-2 rounded-lg transition-colors"
              >
                Next Section
              </button>
            ) : (
              <button
                onClick={() => setView('results')}
                className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-6 py-2 rounded-lg transition-colors"
              >
                View Results
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const { score, gaps } = calculateScore();
  const maturity = getMaturityLevel(score);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => setView('assessment')}
            className="text-foreground-tertiary hover:text-accent"
          >
            ← Back to Assessment
          </button>
          <button
            onClick={exportToMarkdown}
            className="bg-cream/10 hover:bg-cream/20 text-foreground px-4 py-2 rounded-lg transition-colors"
          >
            Export Report
          </button>
          {commandCenterEnabled && !saved && (
            <button
              onClick={handleSaveToCommandCenter}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save to Command Center'}
            </button>
          )}
          {saved && (
            <div className="bg-green-600/20 text-green-400 px-4 py-2 rounded-lg flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Saved to Command Center
            </div>
          )}
          {saveError && (
            <div className="bg-red-600/20 text-red-400 px-4 py-2 rounded-lg text-sm">
              {saveError}
            </div>
          )}
        </div>

        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-foreground mb-4">
            AI Ethics Assessment Results
          </h1>
          <div className={`inline-block px-6 py-3 rounded-lg ${maturity.bg}`}>
            <span className={`text-4xl font-bold ${maturity.color}`}>{score}%</span>
            <p className={`${maturity.color} font-medium mt-1`}>{maturity.level} Maturity</p>
          </div>
        </div>

        <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Principle Scores</h2>
          <div className="space-y-3">
            {sections.map((section) => {
              const sectionScore = getSectionScore(section.id);
              const sectionMaturity = getMaturityLevel(sectionScore);
              return (
                <div key={section.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-foreground-secondary">{section.title}</span>
                    <span className={sectionMaturity.color}>{sectionScore}%</span>
                  </div>
                  <div className="h-2 bg-cream/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        sectionScore >= 90
                          ? 'bg-green-500'
                          : sectionScore >= 75
                          ? 'bg-blue-500'
                          : sectionScore >= 60
                          ? 'bg-yellow-500'
                          : sectionScore >= 40
                          ? 'bg-accent'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${sectionScore}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {gaps.length > 0 && (
          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              Ethics Gaps ({gaps.length})
            </h2>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {gaps.map((gap, index) => (
                <div
                  key={index}
                  className="border border-border-muted rounded-lg p-3"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span className="text-accent text-xs font-medium">
                        {gap.principle}
                      </span>
                      <p className="text-foreground text-sm mt-1">{gap.question}</p>
                    </div>
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded whitespace-nowrap ${
                        gap.status === 'Partial'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}
                    >
                      {gap.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-surface border border-border-muted rounded-lg p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Framework Alignment</h2>
          <div className="text-foreground-secondary text-sm space-y-2">
            <p><span className="text-accent">OECD AI Principles:</span> Human-centered values, transparency, robustness, accountability</p>
            <p><span className="text-accent">EU AI Act:</span> Risk categorization, prohibited practices, high-risk requirements</p>
            <p><span className="text-accent">NIST AI RMF:</span> Govern, Map, Measure, Manage lifecycle approach</p>
            <p><span className="text-accent">IEEE Ethically Aligned Design:</span> Human well-being prioritization</p>
          </div>
        </div>

        <div className="text-center mt-8">
          <button
            onClick={() => {
              setView('landing');
              setCurrentSection(0);
              setAnswers({});
              setSaved(false);
            }}
            className="text-foreground-tertiary hover:text-accent transition-colors"
          >
            Start New Assessment
          </button>
        </div>
      </div>
    </div>
  );
}
